"use client";

import { AlertCircle, TrendingUp, Calendar, User } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface Aktualizacia {
  id: string;
  typ: "zranenie" | "nominacia" | "statistika";
  titulok: string;
  popis: string;
  datum: string;
  hrac?: string;
  dovod?: string;
}

const aktualizacie: Aktualizacia[] = [
  {
    id: "1",
    typ: "zranenie",
    titulok: "Marek Hrivík vypadol z kádra",
    popis:
      "Kapitán reprezentácie sa zranil v NHL a jeho účasť na ZOH 2026 je neistá.",
    datum: "2025-01-28",
    hrac: "Marek Hrivík",
    dovod: "Zranenie v NHL",
  },
  {
    id: "2",
    typ: "statistika",
    titulok: "Juraj Slafkovský v rekordnej forme",
    popis:
      "Slovenský útočník má v NHL 15 gólov a 12 asistencií v posledných 20 zápasoch.",
    datum: "2025-02-01",
    hrac: "Juraj Slafkovský",
  },
  {
    id: "3",
    typ: "nominacia",
    titulok: "Šimon Nemec potvrdil formu",
    popis:
      "Obranca New Jersey Devils pokračuje v skvelých výkonoch v najvyššej súťaži.",
    datum: "2025-02-02",
    hrac: "Šimon Nemec",
  },
];

const TYPOVY_FARBY = {
  zranenie: "bg-red-500/10 text-red-600 border-red-500/20",
  nominacia: "bg-green-500/10 text-green-600 border-green-500/20",
  statistika: "bg-blue-500/10 text-blue-600 border-blue-500/20",
};

const TYPOVY_IKONY = {
  zranenie: AlertCircle,
  nominacia: User,
  statistika: TrendingUp,
};

const TYPOVY_NAZOV = {
  zranenie: "Zranenie",
  nominacia: "Nominácia",
  statistika: "Štatistika",
};

export function AktualizacieSection() {
  const zoradeneAktualizacie = [...aktualizacie].sort(
    (a, b) => new Date(b.datum).getTime() - new Date(a.datum).getTime(),
  );

  const formatDatum = (datum: string) => {
    const date = new Date(datum);
    return date.toLocaleDateString("sk-SK", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const getDniOd = (datum: string) => {
    const dnes = new Date();
    const datumAktualizacie = new Date(datum);
    const rozdiel = dnes.getTime() - datumAktualizacie.getTime();
    const dni = Math.floor(rozdiel / (1000 * 60 * 60 * 24));

    if (dni === 0) return "Dnes";
    if (dni === 1) return "Včera";
    if (dni < 7) return `Pred ${dni} dňami`;
    if (dni < 30) return `Pred ${Math.floor(dni / 7)} týždňami`;
    return `Pred ${Math.floor(dni / 30)} mesiacmi`;
  };

  return (
    <section className="py-16 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-6 py-2 text-xs font-black text-primary mb-8 uppercase tracking-[0.2em] border border-primary/20">
            <Calendar className="h-4 w-4" />
            Aktuálne správy
          </div>
          <h2 className="text-3xl font-black tracking-tighter text-foreground sm:text-4xl mb-4 max-w-3xl mx-auto">
            Najnovšie <span className="text-primary">aktualizácie</span> z
            hokejového sveta
          </h2>
          <p className="text-lg text-muted-foreground/80 max-w-2xl mx-auto leading-relaxed font-medium">
            Sledujte formu hráčov, zranenia a dôležité správy pred nomináciou na
            ZOH 2026.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {zoradeneAktualizacie.map((aktualizacia) => {
            const Ikona = TYPOVY_IKONY[aktualizacia.typ];
            const farba = TYPOVY_FARBY[aktualizacia.typ];
            const typNazov = TYPOVY_NAZOV[aktualizacia.typ];

            return (
              <Card
                key={aktualizacia.id}
                className="border-muted/20 shadow-sm bg-transparent hover:shadow-md transition-all duration-300"
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between mb-2">
                    <Badge
                      variant="outline"
                      className={cn("text-xs font-medium", farba)}
                    >
                      <Ikona className="h-3 w-3 mr-1" />
                      {typNazov}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {getDniOd(aktualizacia.datum)}
                    </span>
                  </div>
                  <CardTitle className="text-lg font-bold text-foreground leading-tight">
                    {aktualizacia.titulok}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                    {aktualizacia.popis}
                  </p>
                  {aktualizacia.hrac && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <User className="h-3 w-3" />
                      <span>Hráč: {aktualizacia.hrac}</span>
                    </div>
                  )}
                  {aktualizacia.dovod && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                      <AlertCircle className="h-3 w-3" />
                      <span>{aktualizacia.dovod}</span>
                    </div>
                  )}
                  <div className="mt-3 pt-3 border-t border-muted/20">
                    <span className="text-xs text-muted-foreground">
                      {formatDatum(aktualizacia.datum)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <p className="text-sm text-muted-foreground italic">
            Aktualizované každý deň • Sledujeme všetkých slovenských hráčov v
            NHL a Európe
          </p>
        </div>
      </div>
    </section>
  );
}
